import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { AssignmentTurnedIn, ArrowUpward } from '@material-ui/icons';
import { BackButton, ButtonLink, Panel, Container } from '../../../../../../Components';
import { FormValues } from '../../../../../../Components/Form/Form.types';
import { CompanyUnitEditForm, ICompanyUnit } from '../../CompanyUnits.Edit';
import { RequirementProcesses } from './RequirementProcesses';
import { RequirementAspects } from './RequirementAspects';
import { RequirementTags } from './RequirementTags';
import { LawHeader } from '../../LawHeader';
import { IRequirement } from '../CustomerRequirements.List';
import { UserContext } from '../../../../../../Context/UserContext/UserContext';
import { AptorApi, useAptorApi, useFetchSelectOptions } from '../../../../../../Api';
import { useSnackbar } from 'notistack';
import { useStyles } from "../../../../AdministrationModule.styles";

const RequirementCompanyUnitForm = (props: { reqId: number; isNew?: boolean; unitId?: number }) => {
  const { reqId, isNew, unitId } = props;
  const { api, abortController } = useAptorApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { getCompanyUnitName } = useContext(UserContext);
  const requirementPath = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/other-requirements/${reqId}`
    : `/organization/law-portal/other-requirements/${reqId}`;
  const [units] = useFetchSelectOptions(
    useCallback(() => api.getCustomerRequirementCompanyUnits(reqId), [reqId, api]),
    abortController,
  );
  const [users] = useFetchSelectOptions(
    useCallback(() => {
      if (isNew) return api.getCustomerUsers();
      return api.getCustomerCompanyUnitUsers(unitId!);
    }, [isNew, unitId, api]),
    abortController,
  );
  const fetcher = useCallback(() => api.getRequirementCompanyUnit<ICompanyUnit>(reqId, unitId!), [reqId, unitId, api]);

  const handleSubmit = async (data: FormValues, api: AptorApi) => {
    var ok = true;
    if (isNew) {
      ok = await api.addRequirementCompanyUnits(reqId, data);
    } else await api.updateRequirementCompanyUnit(reqId, unitId!, { complianceUsers: data.complianceUsers });
    if (!ok && !abortController.current.signal.aborted)
      enqueueSnackbar(
        formatMessage({ id: 'organization.user-unit-mismatch' }, { alias: getCompanyUnitName(formatMessage, true) }),
        { variant: 'warning' },
      );
    navigate(requirementPath);
  };

  return (
    <CompanyUnitEditForm
      isNew={!!isNew}
      units={units}
      users={users}
      dataFetcher={fetcher}
      handleSubmit={handleSubmit}
    />
  );
};

export const AddRequirementCompanyUnit = () => {
  const params = useParams<{ reqid: string }>();
  const location = useLocation();
  const id = parseInt(params.reqid!);
  const backLink = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/other-requirements/${id}`
    : `/organization/law-portal/other-requirements/${id}`;
  return (
    <Container>
      <BackButton link={backLink} />
      <RequirementCompanyUnitForm reqId={id} isNew />
    </Container>
  );
};

export const EditRequirementCompanyUnit = () => {
  const params = useParams<{ reqid: string; unitid: string }>();
  const reqid = parseInt(params.reqid!);
  const unitid = parseInt(params.unitid!);
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const location = useLocation();
  const [name, setName] = useState('');
  const classes = useStyles();
  const backLink = location.pathname.startsWith('/law-portal/settings')
    ? `/law-portal/settings/other-requirements/${reqid}`
    : `/organization/law-portal/other-requirements/${reqid}`;
  const checklistLink = `${backLink}/company-units/${unitid}/checklist`;

  useEffect(() => {
    const initializeName = async () => {
      const info = await api.getRequirement<IRequirement>(reqid);
      if (abortController.current.signal.aborted) {
        return;
      }
      setName(info.name);
    };
    if (name === '') {
      initializeName();
    }
  }, [reqid, name, api, abortController]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Container>
        <BackButton link={backLink} />
        <Grid container spacing={4} direction="column">
          <Grid item>
            <LawHeader name={name} />
          </Grid>
          <Grid item>
            <ButtonLink
              to={checklistLink}
              text={formatMessage({ id: 'law-portal.manage.requirements.checklist' })}
              icon={<AssignmentTurnedIn />}
            />
          </Grid>
          <Grid item>
            <RequirementCompanyUnitForm reqId={reqid} unitId={unitid} />
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.requirements.connectedProcesses" accordion>
              <RequirementProcesses reqid={reqid} unitid={unitid} />
            </Panel>
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.requirements.connectedAspects" accordion>
              <RequirementAspects reqid={reqid} unitid={unitid} />
            </Panel>
          </Grid>
          <Grid item>
            <Panel titleKey="law-portal.manage.requirements.connectedTags" accordion>
              <RequirementTags reqid={reqid} unitid={unitid} />
            </Panel>
          </Grid>
        </Grid>
      </Container>
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </>
  );
};